import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheckIcon, ScaleIcon, DocumentTextIcon, HandRaisedIcon } from '@heroicons/react/24/outline';

const TermsPage = () => {
  const sections = [
    { id: 'definitions', title: '1. Définitions', icon: DocumentTextIcon },
    { id: 'inscription', title: '2. Inscription et compte utilisateur', icon: ShieldCheckIcon },
    { id: 'services', title: '3. Services proposés', icon: HandRaisedIcon },
    { id: 'clients', title: '4. Conditions spécifiques aux Clients', icon: HandRaisedIcon },
    { id: 'smarts', title: '5. Conditions spécifiques aux Smarts', icon: HandRaisedIcon },
    { id: 'evaluation', title: '6. Système d\'évaluation et de notation', icon: HandRaisedIcon },
    { id: 'paiements', title: '7. Paiements et transactions', icon: HandRaisedIcon },
    { id: 'propriete', title: '8. Propriété intellectuelle', icon: HandRaisedIcon },
    { id: 'interdits', title: '9. Comportements interdits', icon: HandRaisedIcon },
    { id: 'suspension', title: '10. Suspension et résiliation de compte', icon: HandRaisedIcon },
    { id: 'responsabilite', title: '11. Limitation de responsabilité', icon: ScaleIcon },
    { id: 'litiges', title: '12. Résolution des litiges', icon: ScaleIcon },
    { id: 'modifications', title: '13. Modifications des CGU', icon: DocumentTextIcon },
    { id: 'droit', title: '14. Droit applicable', icon: ScaleIcon },
    { id: 'contact', title: '15. Contact', icon: DocumentTextIcon },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-12">
      <div className="container-custom max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#111111] rounded-2xl border border-[#252525] overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#F97316] to-[#e8630a] px-8 py-10 text-white">
            <h1 className="text-3xl lg:text-4xl font-display font-bold mb-2">
              Conditions d'utilisation
            </h1>
            <p className="text-white/80 text-lg">
              Les règles qui régissent votre utilisation de la plateforme VADOM.
            </p>
            <div className="mt-4 flex items-center gap-2 text-sm text-white/70">
              <ShieldCheckIcon className="h-5 w-5" />
              <span>Dernière mise à jour : 1er janvier 2025</span>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Introduction */}
            <div className="mb-8 pb-6 border-b border-[#252525]">
              <p className="text-[#AAAAAA] leading-relaxed">
                Bienvenue sur VADOM. En accédant à notre application mobile ou à notre site web, 
                vous acceptez d'être lié par les présentes Conditions Générales d'Utilisation (« CGU »). 
                Si vous n'acceptez pas ces conditions, vous ne devez pas utiliser nos services.
              </p>
              <p className="text-[#AAAAAA] leading-relaxed mt-4">
                Ces CGU constituent un contrat légalement contraignant entre vous (« l'Utilisateur ») 
                et VADOM SAS (« VADOM », « nous »). Veuillez les lire attentivement.
              </p>
            </div>

            {/* Section 1 - Définitions */}
            <section id="definitions" className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
                <DocumentTextIcon className="h-5 w-5 text-[#F97316]" />
                1. Définitions
              </h2>
              <ul className="space-y-2 text-[#AAAAAA] pl-6 list-disc">
                <li><span className="text-white font-medium">VADOM :</span> la société VADOM SAS et sa plateforme (application mobile et site web)</li>
                <li><span className="text-white font-medium">Utilisateur / Client :</span> toute personne qui utilise la plateforme pour rechercher ou réserver des services</li>
                <li><span className="text-white font-medium">Smart :</span> prestataire de services référencé et vérifié sur la plateforme VADOM</li>
                <li><span className="text-white font-medium">Prestation :</span> tout service proposé par un Smart et réservé par un Client via VADOM</li>
                <li><span className="text-white font-medium">Compte :</span> l'espace personnel créé lors de l'inscription sur VADOM</li>
                <li><span className="text-white font-medium">Plateforme :</span> l'application mobile VADOM et le site web www.vadom.tg</li>
              </ul>
            </section>

            {/* Section 2 - Inscription */}
            <section id="inscription" className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">2. Inscription et compte utilisateur</h2>
              <div className="space-y-4 text-[#AAAAAA]">
                <div>
                  <h3 className="text-white font-medium mb-2">2.1 Conditions d'inscription</h3>
                  <ul className="pl-6 list-disc space-y-1">
                    <li>Vous devez avoir au moins 18 ans pour créer un compte VADOM</li>
                    <li>Les informations fournies lors de l'inscription doivent être exactes et à jour</li>
                    <li>Vous ne pouvez créer qu'un seul compte par personne</li>
                    <li>Vous êtes responsable de la confidentialité de vos identifiants de connexion</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">2.2 Responsabilité du compte</h3>
                  <p>Vous êtes entièrement responsable de toutes les activités réalisées depuis votre compte. En cas d'utilisation non autorisée, vous devez nous en informer immédiatement à : <a href="mailto:vadomxpress@gmail.com" className="text-[#F97316] hover:underline">vadomxpress@gmail.com</a></p>
                </div>
              </div>
            </section>

            {/* Section 3 - Services */}
            <section id="services" className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">3. Services proposés</h2>
              <p className="text-[#AAAAAA] leading-relaxed mb-3">
                VADOM est une plateforme de mise en relation. Nous ne sommes pas employeur des Smarts 
                et ne fournissons pas directement les prestations. Notre rôle est de :
              </p>
              <ul className="space-y-1 text-[#AAAAAA] pl-6 list-disc">
                <li>Permettre aux Clients de trouver des prestataires qualifiés</li>
                <li>Offrir aux Smarts un outil de gestion et de promotion de leur activité</li>
                <li>Faciliter les transactions financières entre Clients et Smarts</li>
                <li>Garantir la qualité grâce à notre système de vérification et d'évaluation</li>
              </ul>
            </section>

            {/* Section 4 - Clients */}
            <section id="clients" className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">4. Conditions spécifiques aux Clients</h2>
              <div className="space-y-4 text-[#AAAAAA]">
                <div>
                  <h3 className="text-white font-medium mb-2">4.1 Réservation d'une prestation</h3>
                  <ul className="pl-6 list-disc">
                    <li>La réservation est confirmée après acceptation du Smart et validation du paiement</li>
                    <li>Les informations fournies pour la réservation doivent être exactes</li>
                    <li>Vous devez être présent ou représenté lors de la prestation</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">4.2 Annulation et remboursement</h3>
                  <ul className="pl-6 list-disc">
                    <li>Annulation gratuite jusqu'à 24h avant la prestation</li>
                    <li>Annulation entre 24h et 2h : 50% du montant est dû au Smart</li>
                    <li>Annulation moins de 2h avant ou absence : 100% du montant est dû</li>
                    <li>En cas de prestation non conforme, vous disposez de 48h pour signaler un litige</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">4.3 Comportement attendu</h3>
                  <ul className="pl-6 list-disc">
                    <li>Traiter les Smarts avec respect et dignité</li>
                    <li>Ne pas demander des services illégaux</li>
                    <li>Évaluer honnêtement les prestations reçues</li>
                    <li>Ne pas contacter les Smarts en dehors de la plateforme</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 5 - Smarts */}
            <section id="smarts" className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">5. Conditions spécifiques aux Smarts</h2>
              <div className="space-y-4 text-[#AAAAAA]">
                <div>
                  <h3 className="text-white font-medium mb-2">5.1 Obligations générales</h3>
                  <ul className="pl-6 list-disc">
                    <li>Fournir des informations exactes et maintenir votre profil à jour</li>
                    <li>Posséder toutes les autorisations, licences ou diplômes requis</li>
                    <li>Souscrire une assurance responsabilité civile professionnelle</li>
                    <li>Respecter les délais et qualités de service</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">5.2 Qualité de service</h3>
                  <ul className="pl-6 list-disc">
                    <li>Maintenir une note minimale de 3.5/5 sur les 30 derniers avis</li>
                    <li>En dessous de ce seuil, votre profil sera suspendu après avertissement</li>
                    <li>Taux d'annulation maximum autorisé : 15% sur 30 jours glissants</li>
                    <li>Temps de réponse aux demandes : 4h maximum</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-2">5.3 Tarification et paiements</h3>
                  <ul className="pl-6 list-disc">
                    <li>Vous fixez librement vos tarifs dans le respect des minimums légaux</li>
                    <li>VADOM prélève une commission de 10-15% selon votre plan d'abonnement</li>
                    <li>Les paiements sont versés sous 48h après validation de la prestation</li>
                    <li>En cas de litige, les fonds sont bloqués jusqu'à résolution</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 6-7-8 rapides */}
            <section id="evaluation" className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">6. Système d'évaluation et de notation</h2>
              <ul className="space-y-1 text-[#AAAAAA] pl-6 list-disc">
                <li>Les avis doivent être honnêtes, basés sur une expérience réelle</li>
                <li>Il est interdit d'acheter, d'échanger ou de manipuler des avis</li>
                <li>VADOM se réserve le droit de supprimer les avis abusifs</li>
                <li>Les litiges sur les évaluations sont traités sous 5 jours ouvrés</li>
              </ul>
            </section>

            <section id="paiements" className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">7. Paiements et transactions</h2>
              <ul className="space-y-1 text-[#AAAAAA] pl-6 list-disc">
                <li>Tous les paiements transitent par la plateforme VADOM</li>
                <li>Il est interdit de réaliser des transactions en dehors de la plateforme</li>
                <li>VADOM utilise des prestataires de paiement certifiés</li>
                <li>Les prix affichés sont en Francs CFA (FCFA) toutes taxes incluses</li>
              </ul>
            </section>

            <section id="propriete" className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">8. Propriété intellectuelle</h2>
              <p className="text-[#AAAAAA] leading-relaxed mb-2">
                Tous les éléments de la plateforme VADOM sont protégés par le droit de la propriété intellectuelle.
                Il vous est interdit de :
              </p>
              <ul className="space-y-1 text-[#AAAAAA] pl-6 list-disc">
                <li>Copier, reproduire ou distribuer les éléments sans autorisation</li>
                <li>Utiliser le nom ou le logo VADOM à des fins commerciales sans accord</li>
                <li>Extraire les données de la plateforme de manière automatisée</li>
              </ul>
            </section>

            {/* Section 9 - Comportements interdits */}
            <section id="interdits" className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">9. Comportements interdits</h2>
              <p className="text-[#AAAAAA] leading-relaxed mb-2">Sont strictement interdits sur la plateforme VADOM :</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  'Fraude, usurpation d\'identité ou tromperie',
                  'Harcèlement, menaces ou comportements discriminatoires',
                  'Partage de contenus illégaux, pornographiques ou violents',
                  'Utilisation pour des activités illégales',
                  'Spam, publicité non sollicitée',
                  'Création de faux profils ou faux avis',
                  'Tentatives de piratage des systèmes VADOM'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-[#AAAAAA]">
                    <span className="text-red-500">✗</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 10-11-12-13-14 */}
            <section id="suspension" className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">10. Suspension et résiliation de compte</h2>
              <p className="text-[#AAAAAA] leading-relaxed">
                VADOM se réserve le droit de suspendre ou résilier tout compte qui viole les présentes CGU, 
                présente des informations fausses, est impliqué dans des activités frauduleuses, 
                ou nuit à la réputation de la plateforme. En cas de résiliation pour faute grave, 
                aucun remboursement ne sera effectué.
              </p>
            </section>

            <section id="responsabilite" className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">11. Limitation de responsabilité</h2>
              <p className="text-[#AAAAAA] leading-relaxed">
                VADOM agit en tant qu'intermédiaire et n'est pas responsable : de la qualité des prestations,
                des dommages matériels, des interruptions de service, ou des pertes de données. 
                La responsabilité de VADOM est limitée au montant des commissions perçues sur les 3 derniers mois.
              </p>
            </section>

            <section id="litiges" className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">12. Résolution des litiges</h2>
              <p className="text-[#AAAAAA] leading-relaxed">
                En cas de litige, contactez notre service de médiation à support@vadom.tg. 
                Notre équipe intervient sous 72h ouvrées. Si aucune résolution amiable n'est trouvée, 
                les parties peuvent saisir les juridictions compétentes de Lomé, Togo.
              </p>
            </section>

            <section id="modifications" className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">13. Modifications des CGU</h2>
              <p className="text-[#AAAAAA] leading-relaxed">
                VADOM peut modifier ces CGU à tout moment. Nous vous informerons de toute modification 
                substantielle par notification. Votre utilisation continue vaut acceptation des nouvelles conditions.
              </p>
            </section>

            <section id="droit" className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">14. Droit applicable</h2>
              <p className="text-[#AAAAAA] leading-relaxed">
                Les présentes CGU sont régies par le droit togolais. Tout litige sera soumis aux tribunaux 
                compétents de Lomé, Togo.
              </p>
            </section>

            <section id="contact" className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">15. Contact</h2>
              <div className="bg-[#0a0a0a] rounded-xl p-5 space-y-2">
                <p className="text-[#AAAAAA]">Pour toute question relative aux présentes CGU :</p>
                <p className="text-white">📧 Email : <a href="mailto:vadomxpress@gmail.com" className="text-[#F97316] hover:underline">vadomxpress@gmail.com</a></p>
                <p className="text-white">📍 Adresse : VADOM SAS, Lomé, Togo</p>
                <p className="text-white">🌐 Formulaire de contact : <Link to="/contact" className="text-[#F97316] hover:underline">www.vadom.tech/contact</Link></p>
              </div>
            </section>

            {/* CTA */}
            <div className="mt-10 pt-6 border-t border-[#252525] text-center">
              <h3 className="text-xl font-semibold text-white mb-2">Des questions sur nos conditions ?</h3>
              <p className="text-[#AAAAAA] mb-4">Notre équipe juridique est disponible pour vous répondre.</p>
              <Link to="/contact" className="inline-block bg-[#F97316] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#e8630a] transition">
                Nous contacter
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsPage;